import React, { useState } from 'react';
import { UsersIcon, PlusIcon, EditIcon, UserCheckIcon, UserXIcon, ShieldIcon, UserIcon, AlertCircleIcon, Trash2Icon, LockIcon, SearchIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
export function UsersPage() {
  const {
    users,
    roles,
    addUser,
    updateUser,
    deleteUser
  } = useApp();
  const {
    addActivityLog
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rolId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rolId: roles.length > 0 ? roles[0].id : ''
    });
    setErrors({});
    setEditingUser(null);
    setSubmitError('');
  };
  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };
  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rolId: user.rolId
    });
    setSubmitError('');
    setErrors({});
    setIsModalOpen(true);
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.rolId) newErrors.rolId = 'El rol es requerido';
    if (!editingUser && !formData.password) newErrors.password = 'La contraseña es requerida para nuevos usuarios';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingUser) {
        const updates: Partial<User> = {
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId
        };
        if (formData.password) updates.password = formData.password;
        const result = await updateUser(editingUser.id, updates);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Editó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      } else {
        const result = await addUser({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          rolId: formData.rolId,
          activo: true
        });
        if (!result.success) throw new Error(result.error);
        addActivityLog('Creó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar usuario');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleUserStatus = async (user: User) => {
    await updateUser(user.id, {
      activo: !user.activo
    });
    addActivityLog(user.activo ? 'Desactivó usuario' : 'Activó usuario', `Usuario: ${user.nombre}`);
  };
  const handleDelete = async (user: User) => {
    if (!confirm(`¿Eliminar a ${user.nombre}? Esta acción no se puede deshacer.`)) return;
    await deleteUser(user.id);
    addActivityLog('Eliminó usuario', `Usuario: ${user.nombre}`);
  };
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.nombre
  }));
  const activeUsers = users.filter(u => u.activo);
  const inactiveUsers = users.filter(u => !u.activo);
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.rolNombre.toLowerCase().includes(q);
  });
  const getUserAvatar = (userId: string): string => {
    try {
      return localStorage.getItem(`ventastrack_avatar_${userId}`) || '';
    } catch {
      return '';
    }
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return <div className="space-y-4 sm:space-y-6" data-id="element-4723" data-mp="2yhzd67uvfqt5">
      <PageHeader title="Gestión de Usuarios" subtitle="Administración de vendedores y personal" icon={<UsersIcon className="w-6 h-6" data-id="element-4725" data-mp="2ojjmg5zrajk6" />} data-id="element-4724" data-mp="2yfmyc3ketwkb">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4727" data-mp="2olmmzjyjbv35" />} onClick={openCreateModal} data-id="element-4726" data-mp="2oi8ncjh99zze">
          Nuevo Usuario
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-stagger-1" data-id="element-4728" data-mp="2fjn4en7vgiy3">
        <StatsCard title="Total Usuarios" value={users.length} icon={<UsersIcon className="w-5 h-5" data-id="element-4730" data-mp="25sinvj2jsq15" />} variant="primary" data-id="element-4729" data-mp="2arv9y1llht2o" />
        <StatsCard title="Usuarios Activos" value={activeUsers.length} icon={<UserCheckIcon className="w-5 h-5" data-id="element-4732" data-mp="1vuurypt7hche" />} variant="success" data-id="element-4731" data-mp="20u7h5h7juvn5" />
        <StatsCard title="Usuarios Inactivos" value={inactiveUsers.length} icon={<UserXIcon className="w-5 h-5" data-id="element-4734" data-mp="1rvn714g21uaj" />} variant="danger" data-id="element-4733" data-mp="1vwp4bfqevw9v" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-4735" data-mp="1m70f6101l80e">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4736" data-mp="1m8jwzw1cjr4a" />
        <Input placeholder="Buscar usuarios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-4737" data-mp="189btiqy5hwua" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-4738" data-mp="20yiq3ke1occw">
        <div className="px-4 py-4 sm:px-6 sm:py-5 border-b border-slate-100" data-id="element-4739" data-mp="24wgggqjjx4hv">
          <CardHeader title="Lista de Usuarios" data-id="element-4740" data-mp="1r74wft8ry6ro" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-4741" data-mp="1r4io3re27n8e">
          {filteredUsers.length === 0 ? <div className="p-8 sm:p-16 text-center animate-fade-in" data-id="element-4742" data-mp="1w33ekz3iesjn">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4743" data-mp="2jtfxt647gbpj">
                <UsersIcon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-400" data-id="element-4744" data-mp="2onk5xsp7ips7" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1" data-id="element-4745" data-mp="2xy6eyyuj7v1v">
                No hay usuarios
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-4746" data-mp="37qlxpydnknnt">
                Crea el primer usuario con el botón de arriba
              </p>
            </div> : filteredUsers.map(user => <div key={user.id} className="px-4 py-3 sm:px-6 sm:py-4 hover:bg-slate-50/50 transition-colors" data-id="element-4747" data-mp="2f2q452jdrbv9">
                <div className="flex items-center justify-between" data-id="element-4748" data-mp="2ozurh5oypddn">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0" data-id="element-4749" data-mp="2p15uv4ehwj48">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                backgroundColor: user.avatarUrl ? 'transparent' : '#eef2fc'
              }} data-id="element-4750" data-mp="2k000z393mpt4">
                      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.nombre} className="w-full h-full object-cover" data-id="element-4751" data-mp="1xsm6y0hzc5in" /> : <span className="text-xs sm:text-sm font-bold" style={{
                  color: '#2952C4'
                }} data-id="element-4752" data-mp="2chinyn20ksve">
                          {getInitials(user.nombre)}
                        </span>}
                    </div>
                    <div className="min-w-0" data-id="element-4753" data-mp="2qg8jhy96qkhn">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap" data-id="element-4754" data-mp="r4fjdsr3oz0p">
                        <h4 className="font-semibold text-slate-900 font-heading text-sm sm:text-base truncate" data-id="element-4755" data-mp="w2qtm4lb5bjg">
                          {user.nombre}
                        </h4>
                        <Badge variant={user.activo ? 'success' : 'danger'} data-id="element-4756" data-mp="olav62ug6j87">
                          {user.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Badge variant={user.rolNombre === 'Administrador' ? 'info' : 'default'} data-id="element-4757" data-mp="eu7v94d98aai">
                          {user.rolNombre}
                        </Badge>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-400 mt-0.5 truncate" data-id="element-4758" data-mp="xjeeqtlvh5xd">
                        {user.email} • Creado:{' '}
                        {new Date(user.fechaCreacion).toLocaleDateString('es-DO')}
                      </p>
                    </div>
                  </div>
                  {/* Mobile action buttons (icon-only) */}
                  <div className="flex items-center gap-1 sm:hidden flex-shrink-0 ml-2" data-id="element-4759" data-mp="2i4tqf654m9m0">
                    <button onClick={() => openEditModal(user)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-4760" data-mp="2e3glcy9uyag5">
                      <EditIcon className="w-4 h-4" data-id="element-4761" data-mp="2n1slbqwm6kl9" />
                    </button>
                    <button onClick={() => toggleUserStatus(user)} className={`p-1.5 rounded-lg transition-colors ${user.activo ? 'text-slate-400 hover:text-rose-600 hover:bg-rose-50' : 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'}`} data-id="element-4762" data-mp="31qon5682v1df">
                      {user.activo ? <UserXIcon className="w-4 h-4" data-id="element-4763" data-mp="1geetzq72il77" /> : <UserCheckIcon className="w-4 h-4" data-id="element-4764" data-mp="1bfli227fj295" />}
                    </button>
                    <button onClick={() => handleDelete(user)} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-4765" data-mp="3n37733g9prml">
                      <Trash2Icon className="w-4 h-4" data-id="element-4766" data-mp="3e9kefm7cx4hm" />
                    </button>
                  </div>
                  {/* Desktop action buttons */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-4" data-id="element-4767" data-mp="34cfmt6uqsgtf">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(user)} data-id="element-4768" data-mp="34esdc8ekfzi3">
                      <EditIcon className="w-4 h-4" data-id="element-4769" data-mp="2vdkffrfyp0oj" />
                    </Button>
                    <Button variant={user.activo ? 'danger' : 'success'} size="sm" onClick={() => toggleUserStatus(user)} data-id="element-4770" data-mp="2ze4ej0k3iedb">
                      {user.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-id="element-4771" data-mp="26khoj8jw1dzb">
                      <Trash2Icon className="w-4 h-4 text-rose-400" data-id="element-4772" data-mp="1xhgljgqbk2zv" />
                    </Button>
                  </div>
                </div>
              </div>)}
        </div>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      resetForm();
    }} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} data-id="element-4773" data-mp="2p8mn81lou8r9">
        <ModalBody data-id="element-4774" data-mp="2gf9im2e9hipc">
          <div className="space-y-4" data-id="element-4775" data-mp="2bi11m54unkpo">
            {submitError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-4776" data-mp="2bhraufbzd9ix">
                <AlertCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-4777" data-mp="38yq3fytsumz2" />
                <p className="text-sm text-rose-700" data-id="element-4778" data-mp="3453uohqrb38j">{submitError}</p>
              </div>}
            <Input label="Nombre Completo" placeholder="Ej. Juan Pérez" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} error={errors.nombre} data-id="element-4779" data-mp="3bjo11bsga139" />
            <Input label="Email (para iniciar sesión)" placeholder="juan@empresa.com" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} data-id="element-4780" data-mp="3uhh3ye32qb0n" />
            <Select label="Rol" options={roleOptions} value={formData.rolId} onChange={e => setFormData({
            ...formData,
            rolId: e.target.value
          })} error={errors.rolId} data-id="element-4781" data-mp="d20kh90d7lof" />
            <Input label={editingUser ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña'} type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} error={errors.password} icon={<LockIcon className="w-4 h-4 text-slate-400" data-id="element-4783" data-mp="3vjwci9uw69iq" />} data-id="element-4782" data-mp="4d3a52od44cm" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-4784" data-mp="3qj09rzma0tew">
          <Button variant="secondary" onClick={() => {
          setIsModalOpen(false);
          resetForm();
        }} data-id="element-4785" data-mp="3qigs8k0jg71e">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} data-id="element-4786" data-mp="15vna5rzy53vk">
            {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}